import { MutateOptions, MutationKey, useMutation, UseMutationOptions } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { toast } from 'react-toastify';

import { HttpException, resolveHttpErrorMessage } from '../exceptions/index';
import { createIdempotencyKey, idempotencyKeyHeaders } from '../utils/idempotency-key.util';

const MAX_TRANSIENT_RETRIES = 2;

// Gateway-level failures (backend restart window, proxy 5xx) reach the client without ever
// executing the handler — or without a readable response. With an idempotency key attached the
// backend dedupes replays, so retrying these automatically is safe even for POST.
const isTransientTransportError = (error: unknown): boolean => {
  if (!(error instanceof HttpException)) return true;

  const status = error.response?.status;
  return status === 502 || status === 503 || status === 504;
};

type IdempotentMutationFn<TData, TVariables> = (
  variables: TVariables,
  request: { headers: Record<string, string> },
) => Promise<TData>;

type IdempotentAppMutationProps<
  TData = unknown,
  TError = HttpException,
  TVariables = void,
  TContext = unknown,
> = {
  defaultErrorHandling?: boolean;
  mutationFn: IdempotentMutationFn<TData, TVariables>;
} & Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'mutationFn' | 'mutationKey'>;

export const useIdempotentAppMutation = <
  TData = unknown,
  TError = HttpException,
  TVariables = void,
  TContext = unknown,
>(
  mutationKey: MutationKey = [],
  {
    defaultErrorHandling = true,
    mutationFn,
    ...params
  }: IdempotentAppMutationProps<TData, TError, TVariables, TContext>,
) => {
  const inFlightRef = useRef(false);
  const idempotencyKeyRef = useRef<null | string>(null);

  const mutation = useMutation({
    mutationKey,
    retry: (failureCount, error) =>
      failureCount < MAX_TRANSIENT_RETRIES && isTransientTransportError(error),
    retryDelay: (failureCount) => Math.min(500 * 2 ** failureCount, 2_000),
    ...params,
    // The key is created per submit (beginInFlight) and only cleared on settle, so automatic
    // retries of the same submit replay the SAME key — that is what lets the backend
    // IdempotencyInterceptor recognize them as one operation.
    mutationFn: (variables) => {
      const key = idempotencyKeyRef.current ?? createIdempotencyKey();
      idempotencyKeyRef.current = key;
      return mutationFn(variables, { headers: idempotencyKeyHeaders(key) });
    },
    onError: (error, variables, context) => {
      if (defaultErrorHandling && error instanceof HttpException) {
        toast(resolveHttpErrorMessage(error), {
          position: 'bottom-center',
          type: 'error',
          theme: 'light',
        });
      }

      params.onError?.(error, variables, context);
    },
  });

  const releaseInFlight = useCallback(() => {
    inFlightRef.current = false;
    idempotencyKeyRef.current = null;
  }, []);

  const beginInFlight = useCallback((): boolean => {
    if (inFlightRef.current) return false;
    inFlightRef.current = true;
    idempotencyKeyRef.current = createIdempotencyKey();
    return true;
  }, []);

  const mutate = useCallback(
    (variables: TVariables, options?: MutateOptions<TData, TError, TVariables, TContext>) => {
      if (!beginInFlight()) return;
      mutation.mutate(variables, {
        ...options,
        onSettled: (...args) => {
          releaseInFlight();
          options?.onSettled?.(...args);
        },
      });
    },
    [beginInFlight, mutation, releaseInFlight],
  );

  const mutateAsync = useCallback(
    async (variables: TVariables, options?: MutateOptions<TData, TError, TVariables, TContext>) => {
      if (!beginInFlight()) {
        throw new Error('Duplicate mutation request ignored');
      }

      try {
        return await mutation.mutateAsync(variables, options);
      } finally {
        releaseInFlight();
      }
    },
    [beginInFlight, mutation, releaseInFlight],
  );

  return { ...mutation, mutate, mutateAsync };
};
